import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  IconRegularDelivery,
  IconRegularHouse,
  IconRegularStore,
  IconSolidPlane,
  Text,
} from '@notino/react-styleguide';
import {
  DeliveryType,
  DeliveryVariant,
} from '@notino/shared/definitions/types';

import { Flex } from '@components/Flex';
import { useFormatPrice } from '@components/PriceLabel/useFormattedPrice';
import { IDelivery } from '@containers/ProductListing/model';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dispatchTrackingEvent } from '@context/tracking/utils';

import { messages } from '../messages';
import { getDeliveryDescription, parseThresholdTime } from '../utils';

import { DeliveryDescription, DeliveryItem, DeliveryTab } from './styled';

type Props = {
  defaultTab: DeliveryType;
  personalDeliveries: IDelivery[];
  homeDeliveries: IDelivery[];
  onTabChange: (tab: DeliveryType) => void;
};

const getIconByDelivery = (delivery: IDelivery): JSX.Element => {
  switch (delivery.delivery) {
    case DeliveryType.Personal:
      return <IconRegularStore />;
    case DeliveryType.AirTransport:
      return <IconSolidPlane />;
    default:
      return <IconRegularDelivery />;
  }
};

export const Content = ({
  defaultTab,
  personalDeliveries,
  homeDeliveries,
  onTabChange,
}: Props) => {
  const [activeTab, setActiveTab] = React.useState(defaultTab);
  const formatPrice = useFormatPrice();
  const { formatTime } = useIntl();
  const { getTimeFromInit } = useTrackingContext();

  const withPrice =
    useFeatureFlags().experiment_pd_delivery_variant === DeliveryVariant.Drawer;

  const items =
    activeTab === DeliveryType.Personal ? personalDeliveries : homeDeliveries;

  const hasBothDeliveryTypes =
    personalDeliveries.length > 0 && homeDeliveries.length > 0;

  const handleTabClick = (deliveryType: DeliveryType) => {
    setActiveTab(deliveryType);
    onTabChange(deliveryType);
    dispatchTrackingEvent({
      event: 'element_click',
      element: {
        type: 'delivery_drawer',
        name: deliveryType,
        interaction: 'click',
        timing: getTimeFromInit(),
        mode: 'on',
      },
      _clear: true,
    });
  };

  return (
    <div>
      {hasBothDeliveryTypes && (
        <Flex style={{ position: 'sticky', top: 0, zIndex: 1 }}>
          <DeliveryTab
            selected={activeTab !== DeliveryType.Personal}
            onClick={() => handleTabClick(DeliveryType.Transport)}
          >
            <IconRegularHouse />
            <FormattedMessage {...messages.deliveryDrawerTabHome} />
          </DeliveryTab>

          <DeliveryTab
            selected={activeTab === DeliveryType.Personal}
            onClick={() => handleTabClick(DeliveryType.Personal)}
          >
            <IconRegularStore />
            <FormattedMessage {...messages.deliveryDrawerTabPersonal} />
          </DeliveryTab>
        </Flex>
      )}

      {items.map((item, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <DeliveryItem key={item.name + String(i)}>
          {item.icon ? (
            <img src={item.icon} alt={item.name} />
          ) : (
            getIconByDelivery(item)
          )}
          <Flex direction="column" gap="4px" style={{ flex: 1 }}>
            <Text as="p" typography="labelRegular">
              {item.name}
            </Text>
            {item.thresholdTime && (
              <Text as="p" typography="labelSmall400" color="text.tertiary">
                <FormattedMessage
                  {...messages.deliveryOrderBefore}
                  values={{
                    time: formatTime(parseThresholdTime(item.thresholdTime)),
                  }}
                />
              </Text>
            )}
            <Flex
              gap="4px"
              justify={withPrice ? 'space-between' : 'flex-start'}
              fullWidth={true}
            >
              <DeliveryDescription>
                {getDeliveryDescription(item)}
              </DeliveryDescription>

              {withPrice && (
                <Text typography="labelRegular" color="text.tertiary">
                  {
                    formatPrice({ value: item.price })
                      .formattedPriceWithCurrency
                  }
                </Text>
              )}
            </Flex>
          </Flex>
        </DeliveryItem>
      ))}
    </div>
  );
};
